import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { AITokensExhaustedModal } from '@/components/ui/AITokensExhaustedModal';
import { useGatingStore } from '@/stores/gating.store';
import { LockedFeatureCard } from './LockedFeatureCard';

/**
 * Global host for the two gating modals, mounted ONCE in App (inside the router + modal providers).
 *  - upsell: opened by the axios interceptor on a 403 FEATURE_NOT_AVAILABLE (once per feature and session)
 *  - tokens: opened on a 403 INSUFFICIENT_AI_TOKENS from paths that have no handling of their own
 */
export const GatingHost = () => {
  const upsell = useGatingStore((s) => s.upsell);
  const closeUpsell = useGatingStore((s) => s.closeUpsell);
  const tokensModalOpen = useGatingStore((s) => s.tokensModalOpen);
  const closeTokensModal = useGatingStore((s) => s.closeTokensModal);
  const { t } = useTranslation('common');
  const upsellOpen = upsell !== null;

  // The upsell must ALWAYS be dismissible (a locked user could otherwise be trapped behind it). Modal's own
  // Escape handling yields to any open inline popup or popup owner that is still around (focus on an expanded
  // trigger, a listbox, a tooltip popper), so Escape is also handled here, independent of that check. The
  // upsell has no inner popups, so there is nothing for Escape to yield to.
  useEffect(() => {
    if (!upsellOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !event.isComposing) closeUpsell();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [upsellOpen, closeUpsell]);

  return (
    <>
      <Modal
        open={upsell !== null}
        onOpenChange={(open) => {
          if (!open) closeUpsell();
        }}
        size="sm"
        showCloseButton
        raw
      >
        {upsell && (
          <div className="relative">
            {/* Own close X: Modal renders its close button only in the non-raw layout, and changing that would
                touch every other raw modal. */}
            <button
              type="button"
              onClick={closeUpsell}
              aria-label={t('close')}
              data-testid="gating-upsell-close"
              className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-transparent text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:border-primary-600"
            >
              <Icon name={ICONS.CLOSE} size="sm" ariaHidden />
            </button>
          <LockedFeatureCard
            feature={upsell.feature}
            variant={upsell.variant}
            quota={upsell.quota}
            platform={upsell.platform}
            upsellPlanSlug={upsell.upsellPlanSlug}
            onNavigate={closeUpsell}
            entry="upsell"
            className="border-0 bg-white"
          />
          </div>
        )}
      </Modal>
      <AITokensExhaustedModal open={tokensModalOpen} onClose={closeTokensModal} />
    </>
  );
};

export default GatingHost;
